import {
  collection,
  doc,
  addDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  Timestamp,
  type DocumentSnapshot,
} from 'firebase/firestore'
import { db } from '../firebase'
import { logger } from '../lib/logger'
import type { AuditAction, AuditLog } from '../types'

const COLLECTION = 'audit_log'

/** Audit log entry data */
interface AuditLogData {
  action: AuditAction
  userId?: string
  userName?: string
  targetId?: string
  targetType?: string
  targetName?: string
  details?: Record<string, unknown>
  userAgent?: string
}

/** Audit log query options */
interface AuditQueryOptions {
  action?: AuditAction
  userId?: string
  targetId?: string
  startDate?: string
  endDate?: string
  pageSize?: number
  lastDoc?: DocumentSnapshot | null
}

/** Audit log entry with ID */
interface AuditLogEntry extends AuditLogData {
  id: string
  timestamp: Timestamp
}

/** Paginated audit logs result */
interface PaginatedAuditLogs {
  logs: AuditLogEntry[]
  lastDoc: DocumentSnapshot | null
  hasMore: boolean
}

/** Admin action target */
interface AdminActionTarget {
  id: string
  type: string
  name: string
}

/** Payroll period for logging */
interface PayrollPeriodLog {
  id: string
  startDate: string
  endDate: string
}

/** Session for logging */
interface SessionLog {
  id: string
  userName: string
}

/**
 * Create an audit log entry
 */
export async function createAuditLog(
  action: AuditAction,
  data: Omit<AuditLogData, 'action'>
): Promise<AuditLogEntry | null> {
  try {
    const entry: AuditLogData & { timestamp: Timestamp } = {
      action,
      timestamp: Timestamp.now(),
      ...data,
    }

    // Add device info if available
    if (typeof navigator !== 'undefined') {
      entry.userAgent = navigator.userAgent
    }

    const docRef = await addDoc(collection(db, COLLECTION), entry)
    return { id: docRef.id, ...entry }
  } catch (error) {
    // SECURITY: Use logger instead of console.error to avoid leaking data in production
    logger.error('Error creating audit log', error as Error, { action })
    // Don't throw - audit logging should not break the main flow
    return null
  }
}

/**
 * Log user action
 */
export async function logUserAction(
  action: AuditAction,
  userId: string,
  userName: string,
  details: Record<string, unknown> = {}
): Promise<AuditLogEntry | null> {
  return createAuditLog(action, {
    userId,
    userName,
    details,
  })
}

/**
 * Log admin action
 */
export async function logAdminAction(
  action: AuditAction,
  adminId: string,
  adminName: string,
  target: AdminActionTarget,
  details: Record<string, unknown> = {}
): Promise<AuditLogEntry | null> {
  return createAuditLog(action, {
    userId: adminId,
    userName: adminName,
    targetId: target.id,
    targetType: target.type,
    targetName: target.name,
    details,
  })
}

/**
 * Get audit logs with pagination
 */
export async function getAuditLogs(options: AuditQueryOptions = {}): Promise<PaginatedAuditLogs> {
  const {
    action,
    userId,
    targetId,
    startDate,
    endDate,
    pageSize = 50,
    lastDoc = null,
  } = options

  let q = query(
    collection(db, COLLECTION),
    orderBy('timestamp', 'desc'),
    limit(pageSize)
  )

  if (action) {
    q = query(q, where('action', '==', action))
  }

  if (userId) {
    q = query(q, where('userId', '==', userId))
  }

  if (targetId) {
    q = query(q, where('targetId', '==', targetId))
  }

  if (lastDoc) {
    q = query(q, startAfter(lastDoc))
  }

  const snapshot = await getDocs(q)

  let logs: (AuditLogEntry & { _doc: DocumentSnapshot })[] = snapshot.docs.map(docSnap => ({
    id: docSnap.id,
    ...docSnap.data() as AuditLogData & { timestamp: Timestamp },
    _doc: docSnap, // For pagination
  }))

  // Filter by date range
  if (startDate || endDate) {
    const startTimestamp = startDate ? new Date(startDate).getTime() : 0
    const endTimestamp = endDate ? new Date(endDate).getTime() : Date.now()

    logs = logs.filter(log => {
      const logTime = log.timestamp.toDate().getTime()
      return logTime >= startTimestamp && logTime <= endTimestamp
    })
  }

  const lastDocSnapshot = snapshot.docs[snapshot.docs.length - 1] ?? null

  return {
    logs: logs.map(({ _doc, ...log }) => log),
    lastDoc: lastDocSnapshot,
    hasMore: snapshot.docs.length === pageSize,
  }
}

/**
 * Get recent activity for a user
 */
export async function getUserActivity(userId: string, limitCount = 20): Promise<AuditLogEntry[]> {
  const q = query(
    collection(db, COLLECTION),
    where('userId', '==', userId),
    orderBy('timestamp', 'desc'),
    limit(limitCount)
  )

  const snapshot = await getDocs(q)
  return snapshot.docs.map(docSnap => ({
    id: docSnap.id,
    ...docSnap.data() as AuditLogData & { timestamp: Timestamp },
  }))
}

/** Activity summary item */
interface ActivitySummaryItem {
  action: AuditAction
  count: number
  uniqueUsers: number
}

/**
 * Get activity summary by action type
 */
export async function getActivitySummary(startDate: string, endDate: string): Promise<ActivitySummaryItem[]> {
  const { logs } = await getAuditLogs({
    startDate,
    endDate,
    pageSize: 1000, // Get more for summary
  })

  const summary: Record<string, { action: AuditAction; count: number; users: Set<string> }> = {}

  logs.forEach(log => {
    if (!summary[log.action]) {
      summary[log.action] = {
        action: log.action,
        count: 0,
        users: new Set(),
      }
    }
    const item = summary[log.action]
    if (item) {
      item.count++
      if (log.userId) {
        item.users.add(log.userId)
      }
    }
  })

  return Object.values(summary).map(item => ({
    action: item.action,
    count: item.count,
    uniqueUsers: item.users.size,
  }))
}

/**
 * Search audit logs
 */
export async function searchAuditLogs(
  searchTerm: string,
  options: { pageSize?: number } = {}
): Promise<AuditLogEntry[]> {
  const { pageSize = 50 } = options

  // Get logs and filter client-side (Firestore doesn't support full-text search)
  const { logs } = await getAuditLogs({ pageSize: 500 })

  const term = searchTerm.toLowerCase()
  const filtered = logs.filter(log => {
    return (
      log.action?.toLowerCase().includes(term) ||
      log.userName?.toLowerCase().includes(term) ||
      log.targetName?.toLowerCase().includes(term) ||
      JSON.stringify(log.details)?.toLowerCase().includes(term)
    )
  })

  return filtered.slice(0, pageSize)
}

// ============ PREDEFINED LOG HELPERS ============

/**
 * Log user login
 */
export async function logLogin(userId: string, userName: string): Promise<AuditLogEntry | null> {
  return logUserAction('user.login', userId, userName)
}

/**
 * Log user logout
 */
export async function logLogout(userId: string, userName: string): Promise<AuditLogEntry | null> {
  return logUserAction('user.logout', userId, userName)
}

/**
 * Log clock in
 */
export async function logClockIn(
  userId: string,
  userName: string,
  sessionId: string
): Promise<AuditLogEntry | null> {
  return logUserAction('session.clock_in', userId, userName, {
    sessionId,
  })
}

/**
 * Log clock out
 */
export async function logClockOut(
  userId: string,
  userName: string,
  sessionId: string,
  totalMinutes: number
): Promise<AuditLogEntry | null> {
  return logUserAction('session.clock_out', userId, userName, {
    sessionId,
    totalMinutes,
  })
}

/**
 * Log session edit
 */
export async function logSessionEdit(
  adminId: string,
  adminName: string,
  session: SessionLog,
  changes: Record<string, unknown>
): Promise<AuditLogEntry | null> {
  return logAdminAction(
    'session.edited',
    adminId,
    adminName,
    { id: session.id, type: 'session', name: session.userName },
    { before: session, changes }
  )
}

/**
 * Log payroll generation
 */
export async function logPayrollGeneration(
  adminId: string,
  adminName: string,
  period: PayrollPeriodLog,
  totalAmount: number
): Promise<AuditLogEntry | null> {
  return logAdminAction(
    'payroll.generated',
    adminId,
    adminName,
    { id: period.id, type: 'payroll', name: `${period.startDate} to ${period.endDate}` },
    { totalAmount }
  )
}

/** Session data for delete logging */
interface SessionDeleteLog {
  id: string
  userName: string
  date: string
  totalWorkMinutes?: number | null
}

/**
 * Log session deletion
 */
export async function logSessionDelete(
  adminId: string,
  adminName: string,
  session: SessionDeleteLog,
  reason?: string
): Promise<AuditLogEntry | null> {
  return logAdminAction(
    'session.deleted',
    adminId,
    adminName,
    { id: session.id, type: 'session', name: `${session.userName} - ${session.date}` },
    {
      sessionId: session.id,
      userName: session.userName,
      date: session.date,
      totalWorkMinutes: session.totalWorkMinutes,
      reason: reason || 'No reason provided'
    }
  )
}

// Action type descriptions for UI
export const ACTION_DESCRIPTIONS: Record<AuditAction, string> = {
  'user.login': 'User logged in',
  'user.logout': 'User logged out',
  'user.created': 'User account created',
  'user.updated': 'User profile updated',
  'user.deleted': 'User account deleted',
  'session.clock_in': 'Clocked in',
  'session.clock_out': 'Clocked out',
  'session.break_start': 'Started break',
  'session.break_end': 'Ended break',
  'session.edited': 'Session edited by admin',
  'session.deleted': 'Session deleted',
  'payroll.generated': 'Payroll generated',
  'payroll.approved': 'Payroll approved',
  'system.auto_clock_out': 'Auto clock out (system)',
}
